import React, {useEffect} from 'react';
import {RootRoutes} from "./routes/RootRoutes";
import {useNavigate, useLocation} from "react-router-dom";
import styled from "styled-components";
import 'antd/dist/antd.min.css';
import {Header} from "./components/UI/Header/Header";
import {Footer} from "./components/UI/Footer/Footer";
import {Content} from "antd/es/layout/layout";
import {API} from "./API/API";
import {useAppDispatch, useAppSelector} from "./store/store";
import {Loader} from "./components/UI/Loader/Loader";
import {Role} from "./config/acl/roles/Role";
import {roles} from "./config/acl/roles";
import {pathFromRoute} from "./utils/helpers/acl/pathFromRoute";
import {NotificationsWindow} from "./components/notifications/notificationsCentre/NotificationsWindow";
import {NotificationsProvider} from "./components/notifications/context/NotificationsProvider";
import {connect, disconnect, sendPing} from "./websocket/websocket";
import {NotificationPush} from "./components/notifications/notificationsCentre/NotificationPush";
import {groupApproved} from "./config/acl/roles/groups";
import {getChatList} from "./store/reducers/chatReducer/chatReducer";
import {mediaScreenSizes} from "./utils/constans/styles/screenSizes";
import "leaflet/dist/leaflet.css";
import {getAirportList} from "./store/reducers/airportReducer/airportReducer";
import {getCurrencyList} from "./store/reducers/currencyReducer/currencyReducer";
import {setInAppFromSearchParams, isInAppWebView} from "./utils/constants/mobileAppAuth";
import {MobileBottomNav} from "./components/UI/MobileBottomNav/MobileBottomNav";
import {setAmplitudeUser, resetAmplitudeUser, getOrgTypeFromRole} from "./utils/analytics/amplitude";


const AppStyled = styled.div`
  display: flex;
  background-color: #edeff1;
  flex-direction: column;
  min-height: 100vh;
  position: relative;
  
  @media(max-width: ${mediaScreenSizes.mobile}) {
    background-color: #F3F5F7;
  }
`;

const ContentStyled = styled(Content)`
  display: flex;
  flex-grow: 1;
  //for header space
  margin-top: 64px;

  @media (max-width: ${mediaScreenSizes.mobile}) {
    margin-top: 0; /* header is hidden on mobile */
    padding-top: env(safe-area-inset-top, 0px);
    padding-bottom: 88px; /* space for fixed bottom nav + small gap */
  }
`;


export function App() {
  const dispatch = useAppDispatch();
  const {role, isAuthenticated} = useAppSelector(state => state.auth);
  const user = useAppSelector(state => state.user.oneItemObject.item);
  const company = useAppSelector(state => state.company.oneItemObject.item);
  const navigate = useNavigate();
  const location = useLocation();

  // Set Amplitude user/org when authenticated and user+company are loaded
  useEffect(() => {
    if (!isAuthenticated || role === Role.guest || role === Role.unknown) {
      resetAmplitudeUser();
      return;
    }
    const orgType = getOrgTypeFromRole(role);
    if (orgType && user?.id != null && company?.id != null) {
      setAmplitudeUser({
        userId: user.id,
        userEmail: user.email,
        userRoleInOrg: Role[role] ?? String(role),
        orgType,
        orgId: company.id,
        orgName: company.name,
      });
    }
  }, [isAuthenticated, role, user?.id, user?.email, company?.id, company?.name]);

  useEffect(() => {
    setInAppFromSearchParams(new URLSearchParams(window.location.search));
  }, []);

  // In the mobile app, when logged in and we're on the root URL, open the role's home (first nav item) instead of the marketing homepage.
  useEffect(() => {
    if (
      role !== Role.unknown &&
      role !== Role.guest &&
      isAuthenticated &&
      isInAppWebView() &&
      (location.pathname === '/' || location.pathname === '')
    ) {
      const homePath = pathFromRoute(roles[role].homeRoute);
      if (homePath !== '/') {
        navigate(homePath, { replace: true });
      }
    }
  }, [role, isAuthenticated, location.pathname, navigate]);

  useEffect(() => {
    API.refreshToken().then(() => {
    }).catch(() => {
    });

    // Загружаем таймзоны при старте приложения
    // dispatch(getTimezoneList()); // Закомментировано - загрузка происходит в другом месте

    // Fixes mobile height recognition
    const appHeight = () => {
      const doc = document.documentElement
      doc.style.setProperty('--app-height', `${window.innerHeight}px`)
    }
    window.addEventListener('resize', appHeight)
    appHeight()
  }, [dispatch]);

  useEffect(() => {
    if (isAuthenticated) {
      connect();
      sendPing();
      groupApproved.includes(role) && dispatch(getChatList());
      
      // Загружаем аэропорты только для аутентифицированных пользователей
      dispatch(getAirportList());
      
      // Загружаем валюты только для аутентифицированных пользователей
      dispatch(getCurrencyList());
    } else {
      disconnect();
    }
  }, [isAuthenticated, role, dispatch])

  return (
    <AppStyled>
      {(role !== Role.unknown)
        ?
        <>
          <NotificationsProvider>
            <Header/>
            <NotificationsWindow/>
            <NotificationPush/>
            <ContentStyled>
              <RootRoutes/>
            </ContentStyled>
            <Footer/>
            <MobileBottomNav/>
          </NotificationsProvider>
        </>
        : <Loader/>
      }

    </AppStyled>
  );
}

